import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model/query-params';
import { AnthemService } from '../instances/anthem';
import { AnthemConverter } from '@/converters';

export const useGetAnthem = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_ANTHEM', params],
    async () => {
      const [err, data] = await AnthemService.getAnthem(params);
      if (err) {
        throw err;
      }
      return {
        data: AnthemConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const usePutAnthemMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ANTHEM'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await AnthemService.putAnthem(id, payload);
      if (err) {
        throw err;
      }
      return AnthemConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useGetAnthemById = () => {
  return useMutation({
    mutationKey: ['GET_ANTHEM_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await AnthemService.getAnthemByUUID(uuid);
      if (err) {
        throw err;
      }
      return AnthemConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePostAnthemMutation = () => {
  return useMutation({
    mutationKey: ['POST_ANTHEM'],
    mutationFn: async (payload: any) => {
      const [err, data] = await AnthemService.postAnthem(payload);
      if (err) {
        throw err;
      }
      return AnthemConverter.toDomainFromPostResponse(data);
    },
  });
};

export const useDeleteAnthemMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_ANTHEM'],
    mutationFn: async (id: string) => {
      const [err] = await AnthemService.deleteAnthem(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const useDeleteAnthemFileMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_ANTHEM_FILE'],
    mutationFn: async (id: string) => {
      const [err] = await AnthemService.deleteAnthemFile(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
